import React, { useState } from "react";
import { Button, Card, Col, Form, List, message, Modal, Popconfirm, Row, Select, Space, Tag, Typography } from "antd";
import { CopyOutlined, DeleteOutlined, EditOutlined, EyeOutlined, PlusOutlined, SaveOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { hvoFirestore, vceFirestore } from "configs/FirebaseConfig";
import { FIRESTORE_UTMS_TABLE } from "constants/FirestoreConstant";
import { UTM_MEDIUMS, UTM_SOURCES } from "constants/MarketingConstants";
import urls from "urls";
import Loading from "components/shared-components/Loading";
import { useUTMs } from "data/hooks/useUTMs";
import UTMFormFields from "components/marketing/UTMFormFields";
import dayjs from "dayjs";

const { Option } = Select;
const { Text } = Typography;

const UTMTool = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [shopSourceFilter, setShopSourceFilter] = useState("all");
  const { utms, isLoading, refetch } = useUTMs({ shopSource: shopSourceFilter });
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [detailModal, setDetailModal] = useState({ visible: false, utm: null });
  const [editModal, setEditModal] = useState({ visible: false, utm: null });
  const [editForm] = Form.useForm();
  const [useCustomUrl, setUseCustomUrl] = useState(false);
  const [editUseCustomUrl, setEditUseCustomUrl] = useState(false);

  const transformToUtmValue = (input) => {
    if (!input) return "";
    return input
      .toLowerCase()
      .replace(/[^a-z0-9_\s]/g, "")
      .replace(/\s+/g, "_")
      .trim();
  };

  const generateFullUrl = (utmParams, shopSource = "VCE") => {
    let baseUrl;
    if (utmParams.customUrl) {
      baseUrl = utmParams.customUrl;
    } else {
      baseUrl = shopSource === "VCE" ? "https://hetvleescentrum.nl" : "https://halalvlees-online.nl";
    }

    const params = new URLSearchParams();

    if (utmParams.source) params.append("utm_source", utmParams.source);
    if (utmParams.medium) params.append("utm_medium", utmParams.medium);
    if (utmParams.campaign) params.append("utm_campaign", utmParams.campaign);
    if (utmParams.term) params.append("utm_term", utmParams.term);
    if (utmParams.content) params.append("utm_content", utmParams.content);

    return `${baseUrl}?${params.toString()}`;
  };

  const handleSaveUtm = async (values) => {
    setLoading(true);
    try {
      const firestore = values.shopSource === "VCE" ? vceFirestore : hvoFirestore;

      const utmData = {
        name: values.name,
        source: values.sourceType === "other" ? values.sourceCustom : values.sourceType,
        medium: values.mediumType === "other" ? values.mediumCustom : values.mediumType,
        campaign: transformToUtmValue(values.campaign),
        term: transformToUtmValue(values.term),
        content: transformToUtmValue(values.content),
        customUrl: useCustomUrl ? values.customUrl || null : null,
        createdAt: dayjs().unix(),
      };

      await firestore.collection(FIRESTORE_UTMS_TABLE).add(utmData);
      message.success(t("utm_saved"));
      form.resetFields();
      setUseCustomUrl(false);
      refetch();
    } catch (error) {
      console.error("Error saving UTM:", error);
      message.error(t("error_saving_utm"));
    } finally {
      setLoading(false);
    }
  };

  const handleEditUtm = async (values) => {
    setLoading(true);
    try {
      const firestore = editModal.utm.shopSource === "VCE" ? vceFirestore : hvoFirestore;

      const utmData = {
        name: values.name,
        source: values.sourceType === "other" ? values.sourceCustom : values.sourceType,
        medium: values.mediumType === "other" ? values.mediumCustom : values.mediumType,
        campaign: transformToUtmValue(values.campaign),
        term: transformToUtmValue(values.term),
        content: transformToUtmValue(values.content),
        customUrl: editUseCustomUrl ? values.customUrl || null : null,
        updatedAt: dayjs().unix(),
      };

      await firestore.collection(FIRESTORE_UTMS_TABLE).doc(editModal.utm.id).update(utmData);
      message.success(t("utm_updated"));
      setEditModal({ visible: false, utm: null });
      editForm.resetFields();
      setEditUseCustomUrl(false);
      refetch();
    } catch (error) {
      console.error("Error updating UTM:", error);
      message.error(t("error_updating_utm"));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUtm = async (utm) => {
    try {
      const firestore = utm.shopSource === "VCE" ? vceFirestore : hvoFirestore;
      await firestore.collection(FIRESTORE_UTMS_TABLE).doc(utm.id).delete();
      message.success(t("utm_deleted"));
      refetch();
    } catch (error) {
      console.error("Error deleting UTM:", error);
      message.error(t("error_deleting_utm"));
    }
  };

  const copyUrl = (utm) => {
    const url = generateFullUrl(utm, utm.shopSource);
    navigator.clipboard.writeText(url);
    message.success(t("url_copied"));
  };

  const showDetail = (utm) => {
    setDetailModal({ visible: true, utm });
  };

  const showEdit = (utm) => {
    setEditModal({ visible: true, utm });
    setEditUseCustomUrl(!!utm.customUrl);
    editForm.setFieldsValue({
      name: utm.name,
      sourceType: UTM_SOURCES.find((s) => s.value === utm.source) ? utm.source : "other",
      sourceCustom: UTM_SOURCES.find((s) => s.value === utm.source) ? "" : utm.source,
      mediumType: UTM_MEDIUMS.find((m) => m.value === utm.medium) ? utm.medium : "other",
      mediumCustom: UTM_MEDIUMS.find((m) => m.value === utm.medium) ? "" : utm.medium,
      campaign: utm.campaign,
      term: utm.term,
      content: utm.content,
      customUrl: utm.customUrl || "",
    });
  };

  const createCampaignWithUtm = (utm) => {
    const campaignData = {
      utmSource: utm.source,
      utmMedium: utm.medium,
      utmCampaign: utm.campaign,
      utmTerm: utm.term,
      utmContent: utm.content,
    };

    navigate(urls.addCampaign, { state: { utmData: campaignData } });
  };

  if (isLoading) {
    return <Loading cover="content" />;
  }

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <h2>{t("utm_tool")}</h2>
        </Col>
        <Col>
          <Select
            placeholder={t("shop_source")}
            value={shopSourceFilter}
            onChange={setShopSourceFilter}
            style={{ width: 150 }}
          >
            <Option value="all">{t("all_shops")}</Option>
            <Option value="VCE">VCE</Option>
            <Option value="HVO">HVO</Option>
          </Select>
        </Col>
      </Row>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={12}>
          <Card title={t("create_utm")}>
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSaveUtm}
              initialValues={{ sourceType: "facebook", mediumType: "social", shopSource: "VCE" }}
            >
              <UTMFormFields
                form={form}
                useCustomUrl={useCustomUrl}
                setUseCustomUrl={setUseCustomUrl}
                showShopSource={true}
                shopSourceDisabled={false}
              />

              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading} icon={<SaveOutlined />}>
                  {t("save_utm")}
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title={t("saved_utms")}>
            <List
              dataSource={utms}
              renderItem={(utm) => (
                <List.Item
                  actions={[
                    <Button size="small" icon={<EyeOutlined />} onClick={() => showDetail(utm)} />,
                    <Button size="small" icon={<EditOutlined />} onClick={() => showEdit(utm)} />,
                    <Button size="small" icon={<CopyOutlined />} onClick={() => copyUrl(utm)} />,
                    <Popconfirm title={t("confirm_delete")} onConfirm={() => handleDeleteUtm(utm)}>
                      <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>,
                  ]}
                >
                  <List.Item.Meta
                    title={
                      <Space>
                        {utm.name}
                        <Tag color={utm.shopSource === "VCE" ? "cyan" : "magenta"}>{utm.shopSource}</Tag>
                      </Space>
                    }
                    description={
                      <Space>
                        <Tag>{utm.source}</Tag>
                        <Tag>{utm.medium}</Tag>
                        <Tag>{utm.campaign}</Tag>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>

      <Modal
        title={t("utm_details")}
        open={detailModal.visible}
        onCancel={() => setDetailModal({ visible: false, utm: null })}
        footer={[
          <Button key="copy" icon={<CopyOutlined />} onClick={() => copyUrl(detailModal.utm)}>
            {t("copy_url")}
          </Button>,
          <Button
            key="campaign"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => createCampaignWithUtm(detailModal.utm)}
          >
            {t("create_campaign_with_utm")}
          </Button>,
        ]}
      >
        {detailModal.utm && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Text strong>{t("shop")}:</Text>{" "}
              <Tag color={detailModal.utm.shopSource === "VCE" ? "cyan" : "magenta"}>{detailModal.utm.shopSource}</Tag>
            </div>
            <div style={{ marginBottom: 16 }}>
              <Text strong>Pagina:</Text>
              <br />
              <Text code style={{ wordBreak: "break-all" }}>
                {detailModal.utm.customUrl ||
                  (detailModal.utm.shopSource === "VCE"
                    ? "https://hetvleescentrum.nl"
                    : "https://halalvlees-online.nl")}
              </Text>
            </div>
            <div style={{ marginBottom: 16 }}>
              <Text strong>{t("complete_url")}:</Text>
              <br />
              <Text code copyable style={{ wordBreak: "break-all" }}>
                {generateFullUrl(detailModal.utm, detailModal.utm.shopSource)}
              </Text>
            </div>

            <div style={{ marginBottom: 8 }}>
              <Text strong>UTM Source:</Text> <Tag>{detailModal.utm.source}</Tag>
            </div>
            <div style={{ marginBottom: 8 }}>
              <Text strong>UTM Medium:</Text> <Tag>{detailModal.utm.medium}</Tag>
            </div>
            <div style={{ marginBottom: 8 }}>
              <Text strong>UTM Campaign:</Text> <Tag>{detailModal.utm.campaign}</Tag>
            </div>
            {detailModal.utm.term && (
              <div style={{ marginBottom: 8 }}>
                <Text strong>UTM Term:</Text> <Tag>{detailModal.utm.term}</Tag>
              </div>
            )}
            {detailModal.utm.content && (
              <div style={{ marginBottom: 8 }}>
                <Text strong>UTM Content:</Text> <Tag>{detailModal.utm.content}</Tag>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        title={t("edit_utm")}
        open={editModal.visible}
        onCancel={() => {
          setEditModal({ visible: false, utm: null });
          editForm.resetFields();
          setEditUseCustomUrl(false);
        }}
        footer={null}
      >
        <Form form={editForm} layout="vertical" onFinish={handleEditUtm}>
          <UTMFormFields
            form={editForm}
            useCustomUrl={editUseCustomUrl}
            setUseCustomUrl={setEditUseCustomUrl}
            showShopSource={false}
            shopSourceDisabled={true}
          />

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                {t("update_utm")}
              </Button>
              <Button
                onClick={() => {
                  setEditModal({ visible: false, utm: null });
                  editForm.resetFields();
                  setEditUseCustomUrl(false);
                }}
              >
                {t("cancel")}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UTMTool;
